import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@boozallen-uip/uswds-react-core";

const EDITOR_CONTAINER_SELECTOR = '[data-testid="acr-editor"]';

function getEditorDetails() {
  const container = document.querySelector(EDITOR_CONTAINER_SELECTOR);
  return container ? container.querySelectorAll("details") : [];
}

function setButtonStatus() {
  const details = getEditorDetails();
  const someCollapsed = Array.from(details).some((el) => !el.hasAttribute("open"));
  const someExpanded = Array.from(details).some((el) => el.hasAttribute("open"));
  return { someCollapsed, someExpanded };
}

/**
 * Expand All / Collapse All for <details> sections. Ported from GSA openacr-editor ExpandCollapseAll.svelte.
 * Scoped to the ACR editor container so only editor sections are toggled.
 * Re-runs when the route changes so the button state matches the new page's details (e.g. new chapter = all collapsed).
 */
export default function ExpandCollapseAll() {
  const location = useLocation();
  const [status, setStatus] = useState({ someCollapsed: false, someExpanded: false });

  const refresh = useCallback(() => {
    setStatus(setButtonStatus());
  }, []);

  useEffect(() => {
    const details = getEditorDetails();
    const timeouts = [];
    const handlers = Array.from(details).map((el) => {
      const handler = () => {
        const t = setTimeout(refresh, 100);
        timeouts.push(t);
      };
      el.addEventListener("toggle", handler);
      return { el, handler };
    });
    refresh();
    const deferredRefresh = setTimeout(refresh, 0);
    timeouts.push(deferredRefresh);
    return () => {
      handlers.forEach(({ el, handler }) => el.removeEventListener("toggle", handler));
      timeouts.forEach((t) => clearTimeout(t));
    };
  }, [refresh, location.pathname]);

  const expandAll = useCallback(() => {
    getEditorDetails().forEach((el) => el.setAttribute("open", ""));
    setStatus(setButtonStatus());
  }, []);

  const collapseAll = useCallback(() => {
    getEditorDetails().forEach((el) => el.removeAttribute("open"));
    setStatus(setButtonStatus());
  }, []);

  const hasDetails = status.someCollapsed || status.someExpanded;

  return (
    <div className="my-2 flex gap-2">
      {status.someCollapsed && (
        <Button onClick={expandAll} outline>
          + Expand All Sections
        </Button>
      )}
      {status.someExpanded && (
        <Button onClick={collapseAll} outline>
          − Collapse All Sections
        </Button>
      )}
      {!hasDetails && (
        <span className="text-gray-600 text-sm">No expandable sections on this page.</span>
      )}
    </div>
  );
}
